import { QueryFilterFieldSelectButton } from '@modules/queryBuilder/components/QueryFilter/QueryFilterFieldSelectButton';
import { QueryFilterOperatorSelectButton } from '@modules/queryBuilder/components/QueryFilter/QueryFilterOperatorSelectButton';
import { QueryFilterValueSelectButton } from '@modules/queryBuilder/components/QueryFilter/QueryFilterValueSelectButton';
import { QueryFilterActions } from '@modules/queryBuilder/components/QueryFilterActions';
import { QueryFilterConjunctionSelectButton } from '@modules/queryBuilder/components/QueryFilterConjunctionSelectButton';
import { trackQBEvent } from '@modules/queryBuilder/events';
import {
  getNewFilterNode,
  removeQueryFilterNode,
  upsertQueryFilterNode,
} from '@modules/queryBuilder/helpers';
import { getDepthOfFilter } from '@modules/queryBuilder/helpers/getDepthOfFilter';
import { QueryBuilderActionType } from '@modules/queryBuilder/store/queryBuilderActionType';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { Stack } from '@mui/material';
import { v4 } from 'uuid';
import {
  NormalisedFilter,
  NormalisedFilterGroup,
} from 'v2/querybuilder/types/querystore';

interface QueryFilterProps {
  filter: NormalisedFilter;
}

export function QueryFilter({ filter }: QueryFilterProps) {
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const nodes = useQueryBuilderStore(({ query }) => query.nodes);

  const onFieldChange = async (newField: string | null) => {
    dispatch({
      type: QueryBuilderActionType.SET_METADATA,
      payload: {
        filterIDUnderInspect: filter.id,
      },
    });
  };

  const removeFilter = () => {
    const updatedNodes = removeQueryFilterNode({
      node: { ...filter },
      nodes,
      parentID: filter.parent,
    });

    dispatch({
      type: QueryBuilderActionType.SET_QUERY,
      payload: {
        nodes: updatedNodes,
      },
    });

    trackQBEvent({
      eventName: 'Query Builder Action Event',
      action: {
        type: 'REMOVE_FILTER',
        display: '',
        searchMetadata: '',
      },
    });
  };

  const addNestedFilter = () => {
    const newGroupNode: NormalisedFilterGroup = {
      id: filter.id,
      type: 'group',
      parent: filter.parent,
      conjunction: 'AND',
      children: [],
    };
    const updatedFilterNode = {
      ...filter,
      id: v4(),
      parent: newGroupNode.id,
    };
    const newFilterNode = getNewFilterNode({ parentID: newGroupNode.id });

    const nodesWithNewGroup = upsertQueryFilterNode({
      node: newGroupNode,
      nodesByID: nodes,
      parentID: newGroupNode.parent,
    });

    const nodesWithModifiedFilter = upsertQueryFilterNode({
      node: updatedFilterNode,
      nodesByID: nodesWithNewGroup,
      parentID: updatedFilterNode.parent,
    });

    const nodesWithNewFilter = upsertQueryFilterNode({
      node: newFilterNode,
      nodesByID: nodesWithModifiedFilter,
      parentID: newFilterNode.parent,
    });

    dispatch({
      type: QueryBuilderActionType.SET_QUERY,
      payload: {
        nodes: nodesWithNewFilter,
      },
    });

    dispatch({
      type: QueryBuilderActionType.SET_METADATA,
      payload: {
        filterIDUnderInspect: newFilterNode.id,
      },
    });

    trackQBEvent({
      eventName: 'Query Builder Action Event',
      action: {
        type: 'ADD_FILTER',
        field: newFilterNode.field,
        display: '',
        searchMetadata: '',
      },
    });
  };

  const parentNode = nodes[filter.parent];
  const isFirstChild = parentNode?.children?.[0] === filter.id;
  const shouldShowConjuction = parentNode.type === 'group' && !isFirstChild;

  const nestedFilterDepth = getDepthOfFilter(nodes, filter.id);

  return (
    <Stack
      className="query-filter"
      data-filter-id={filter.id}
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        [`.filter-actions-${filter.id}`]: {
          visibility: 'hidden',
        },
        '&:hover': {
          [`.filter-actions-${filter.id}`]: {
            visibility: 'visible',
          },
        },
      }}
    >
      {shouldShowConjuction && (
        <QueryFilterConjunctionSelectButton filter={parentNode} />
      )}
      <QueryFilterFieldSelectButton filter={filter} onChange={onFieldChange} />
      <QueryFilterOperatorSelectButton filter={filter} />
      <QueryFilterValueSelectButton
        key={`${filter.field}_value`}
        filter={filter}
      />
      <QueryFilterActions
        id={filter.id}
        onAddNestedFilter={addNestedFilter}
        onRemoveFilter={removeFilter}
        shouldAllowNestedFilter={nestedFilterDepth < 3}
      />
    </Stack>
  );
}
